/**
 * PlotShareView — anonymous, read-only view for a shared plot.
 *
 * FXC-8720: This is the frontend route guard for plot share tokens.
 * URL: /plot-share/:token
 *
 * CONTRACT (for FXC-8724 viewer agent):
 *
 * This component resolves the token and exposes the scoped plot context
 * via the `PlotShareContext`. The FXC-8724 viewer builds the visual UI
 * on top of this context.
 *
 * Context shape (PlotShareContextValue):
 *   {
 *     token: string,                  // raw token (for query-string auth on sub-requests)
 *     scope: PlotShareScope,          // { plot_id, project_id, branch, allowed_points }
 *     plotData: SharedPlotDataResponse | null,  // null while loading
 *     isLoading: boolean,
 *     error: string | null,
 *   }
 *
 * Security properties:
 * - No auth session is requested or required.
 * - The token is taken from the URL path param — it is NOT placed in
 *   localStorage, sessionStorage, or any Authorization header.
 * - The scope is fetched from /api/plots/share/resolve (anonymous endpoint).
 * - Plot data is fetched from /api/plots/share/<plot_id>/data?token=<raw>
 *   (also anonymous, token in query string only).
 * - No project navigation, no other plots, no write endpoints are reachable
 *   from this view.
 * - If the token is invalid/expired/revoked, renders an error screen.
 *
 * How FXC-8724 consumes this:
 *   import { usePlotShareContext } from "../pages/PlotShareView";
 *   const { scope, plotData, token } = usePlotShareContext();
 *   // Render your read-only plot viewer using scope + plotData.
 *   // For flowfield: GET /api/plots/share/${scope.plot_id}/flowfield?token=${token}
 *   // For chat:      GET /api/plots/share/${scope.plot_id}/chat?token=${token}
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { Box, Heading, Spinner, Text } from "@radix-ui/themes";

import {
  fetchSharedPlotData,
  resolvePlotShareToken,
  type PlotShareScope,
  type SharedPlotDataResponse,
} from "../api/plotShare";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface PlotShareContextValue {
  /** Raw token — use in query-string for sub-requests (flowfield, chat). */
  token: string;
  /** The minimal scope yielded by the token resolve step. */
  scope: PlotShareScope | null;
  /** Full plot payload — null while loading. */
  plotData: SharedPlotDataResponse | null;
  isLoading: boolean;
  error: string | null;
}

const PlotShareContext = createContext<PlotShareContextValue | null>(null);

export function usePlotShareContext(): PlotShareContextValue {
  const ctx = useContext(PlotShareContext);
  if (!ctx) {
    throw new Error(
      "usePlotShareContext must be used within a PlotShareView route",
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

interface PlotShareViewProps {
  /** Slot for the FXC-8724 viewer — rendered once token + data are resolved. */
  children?: React.ReactNode;
}

export default function PlotShareView({ children }: PlotShareViewProps) {
  const { token = "" } = useParams<{ token: string }>();

  const [scope, setScope] = useState<PlotShareScope | null>(null);
  const [plotData, setPlotData] = useState<SharedPlotDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setError("No share token in URL.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: resolve token → scope (anonymous)
      const resolved = await resolvePlotShareToken(token);
      if (!resolved) {
        setError("This share link is invalid, expired, or has been revoked.");
        setIsLoading(false);
        return;
      }
      setScope(resolved);

      // Step 2: fetch the plot data scoped to this token
      const data = await fetchSharedPlotData(resolved.plot_id, token);
      setPlotData(data);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to load shared plot.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <Box p="6" style={{ textAlign: "center", minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Spinner size="3" />
        <Text mt="3" color="gray">Loading shared plot&hellip;</Text>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error || !scope) {
    return (
      <Box p="6" style={{ maxWidth: 560, margin: "0 auto" }}>
        <Heading size="4" color="red" mb="2">
          Share Link Unavailable
        </Heading>
        <Text color="gray">
          {error ?? "This share link could not be resolved."}
        </Text>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Resolved — expose context to children (the FXC-8724 viewer)
  // ---------------------------------------------------------------------------

  const ctxValue: PlotShareContextValue = {
    token,
    scope,
    plotData,
    isLoading,
    error,
  };

  return (
    <PlotShareContext.Provider value={ctxValue}>
      {children ?? (
        // Default: minimal read-only plot placeholder.
        // FXC-8724 will replace this with the full viewer component.
        <Box p="6" style={{ maxWidth: 900, margin: "0 auto" }}>
          <Heading size="5" mb="3">
            {(plotData?.plot as Record<string, unknown>)?.title as string ?? "Shared Plot"}
          </Heading>
          <Text size="2" color="gray" mb="4" as="p">
            Project: {scope.project_id} &bull; Branch: {scope.branch}
          </Text>
          <Box
            p="4"
            style={{
              background: "var(--gray-2)",
              borderRadius: "var(--radius-3)",
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              overflowX: "auto",
            }}
          >
            {JSON.stringify(plotData?.plot ?? {}, null, 2)}
          </Box>
          <Text size="1" color="gray" mt="3" as="p">
            Full viewer coming in FXC-8724.
          </Text>
        </Box>
      )}
    </PlotShareContext.Provider>
  );
}
